import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowRight, Users, CheckSquare, Building2, AlertTriangle, Loader2, ArrowLeftRight } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { STATUS_COLORS_SOFT as STATUS_COLORS, STATUS_LABELS_SHORT as STATUS_LABELS } from '../utils/constants';

export default function Transfer() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Task transfer state
  const [taskFrom, setTaskFrom] = useState('');
  const [taskTo, setTaskTo] = useState('');
  const [excludeCompleted, setExcludeCompleted] = useState(true);
  const [taskPreview, setTaskPreview] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [taskSelectAll, setTaskSelectAll] = useState(true);
  const [showTaskConfirm, setShowTaskConfirm] = useState(false);
  const [taskTransferring, setTaskTransferring] = useState(false);
  // Client transfer state
  const [clientFrom, setClientFrom] = useState('');
  const [clientTo, setClientTo] = useState('');
  const [clientPreview, setClientPreview] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());
  const [clientSelectAll, setClientSelectAll] = useState(true);
  const [showClientConfirm, setShowClientConfirm] = useState(false);
  const [clientTransferring, setClientTransferring] = useState(false);

  useEffect(() => {
    api.get('/users').then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nonSuperAdmins = users.filter(u => u.role !== 'super_admin' && u.is_active);

  // ---- Task Transfer ----
  const previewTasks = async () => {
    if (!taskFrom || !taskTo) { toast.error('Select both users'); return; }
    if (taskFrom === taskTo) { toast.error('From and To must be different'); return; }
    try {
      const res = await api.post('/transfer/tasks/preview', {
        from_user_id: taskFrom, to_user_id: taskTo, exclude_completed: excludeCompleted,
      });
      setTaskPreview(res.data);
      const allIds = new Set(res.data.tasks.map(t => t.id));
      setSelectedTaskIds(allIds);
      setTaskSelectAll(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to preview'); }
  };

  const toggleTaskSelect = (id) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setTaskSelectAll(false);
  };

  const toggleAllTasks = () => {
    if (taskSelectAll) {
      setSelectedTaskIds(new Set());
      setTaskSelectAll(false);
    } else {
      setSelectedTaskIds(new Set(taskPreview.tasks.map(t => t.id)));
      setTaskSelectAll(true);
    }
  };

  const executeTaskTransfer = async () => {
    setTaskTransferring(true);
    try {
      const ids = taskSelectAll ? null : Array.from(selectedTaskIds);
      const res = await api.post('/transfer/tasks/execute', {
        from_user_id: taskFrom, to_user_id: taskTo, exclude_completed: excludeCompleted,
        task_ids: ids,
      });
      toast.success(res.data.message);
      setShowTaskConfirm(false);
      setTaskPreview(null);
      setTaskFrom(''); setTaskTo('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setTaskTransferring(false); }
  };

  // ---- Client Transfer ----
  const previewClients = async () => {
    if (!clientFrom || !clientTo) { toast.error('Select both users'); return; }
    if (clientFrom === clientTo) { toast.error('From and To must be different'); return; }
    try {
      const res = await api.post('/transfer/clients/preview', {
        from_user_id: clientFrom, to_user_id: clientTo,
      });
      setClientPreview(res.data);
      const allIds = new Set(res.data.clients.map(c => c.id));
      setSelectedClientIds(allIds);
      setClientSelectAll(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to preview'); }
  };

  const toggleClientSelect = (id) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setClientSelectAll(false);
  };

  const toggleAllClients = () => {
    if (clientSelectAll) {
      setSelectedClientIds(new Set());
      setClientSelectAll(false);
    } else {
      setSelectedClientIds(new Set(clientPreview.clients.map(c => c.id)));
      setClientSelectAll(true);
    }
  };

  const executeClientTransfer = async () => {
    setClientTransferring(true);
    try {
      const ids = clientSelectAll ? null : Array.from(selectedClientIds);
      const res = await api.post('/transfer/clients/execute', {
        from_user_id: clientFrom, to_user_id: clientTo,
        client_ids: ids,
      });
      toast.success(res.data.message);
      setShowClientConfirm(false);
      setClientPreview(null);
      setClientFrom(''); setClientTo('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setClientTransferring(false); }
  };

  const getUserName = (id) => users.find(u => u.id === id)?.full_name || '';

  if (loading) return <div className="p-8 text-center text-slate-400">Loading...</div>;

  const transferCount = taskSelectAll ? (taskPreview?.task_count || 0) : selectedTaskIds.size;
  const clientTransferCount = clientSelectAll ? (clientPreview?.client_count || 0) : selectedClientIds.size;

  return (
    <div className="p-8 min-h-full bg-slate-50" data-testid="transfer-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-3" data-testid="transfer-heading">
          <ArrowLeftRight className="h-8 w-8 text-[#5B6FD6]" /> Bulk Transfer
        </h1>
        <p className="text-slate-500">Transfer tasks and clients between team members</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ---- Task Transfer Card ---- */}
        <Card className="border-slate-200 shadow-sm" data-testid="task-transfer-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-[#5B6FD6]" /> Task Transfer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">From</label>
                <Select value={taskFrom} onValueChange={v => { setTaskFrom(v); setTaskPreview(null); }}>
                  <SelectTrigger data-testid="task-from-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{nonSuperAdmins.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400 mb-2" />
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">To</label>
                <Select value={taskTo} onValueChange={v => { setTaskTo(v); setTaskPreview(null); }}>
                  <SelectTrigger data-testid="task-to-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{nonSuperAdmins.filter(u => u.id !== taskFrom).map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={excludeCompleted} onCheckedChange={c => { setExcludeCompleted(c); setTaskPreview(null); }} data-testid="exclude-completed-checkbox" />
              <label className="text-sm text-slate-600">Exclude completed tasks</label>
            </div>
            <Button onClick={previewTasks} className="w-full bg-[#5B6FD6] hover:bg-[#4A5BC4]" data-testid="preview-tasks-btn"
              disabled={!taskFrom || !taskTo || taskFrom === taskTo}>
              Preview Transfer
            </Button>

            {taskPreview && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="task-preview-list">
                <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b">
                  <span className="text-sm font-medium text-slate-700">
                    {taskPreview.from_user} <ArrowRight className="h-3 w-3 inline mx-1" /> {taskPreview.to_user}
                  </span>
                  <Badge variant="outline">{taskPreview.task_count} tasks</Badge>
                </div>
                {taskPreview.tasks.length === 0 ? (
                  <div className="p-4 text-sm text-slate-400 text-center">No tasks to transfer</div>
                ) : (
                  <>
                    <div className="px-4 py-2 border-b bg-white flex items-center gap-2">
                      <Checkbox checked={taskSelectAll} onCheckedChange={toggleAllTasks} data-testid="task-select-all" />
                      <span className="text-xs text-slate-500">Select All ({selectedTaskIds.size}/{taskPreview.tasks.length})</span>
                    </div>
                    <div className="max-h-[250px] overflow-y-auto">
                      {taskPreview.tasks.map(t => (
                        <div key={t.id} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 hover:bg-slate-50" data-testid={`task-item-${t.id}`}>
                          <Checkbox checked={selectedTaskIds.has(t.id)} onCheckedChange={() => toggleTaskSelect(t.id)} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 truncate">{t.title}</p>
                            <p className="text-xs text-slate-400">{t.client_name} · {t.service_name}</p>
                          </div>
                          <Badge className={`text-[10px] ${STATUS_COLORS[t.status] || 'bg-slate-100'}`}>{STATUS_LABELS[t.status] || t.status}</Badge>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-3 bg-slate-50 border-t">
                      <Button onClick={() => setShowTaskConfirm(true)} className="w-full bg-[#5B6FD6] hover:bg-[#4A5BC4]"
                        disabled={transferCount === 0} data-testid="confirm-task-transfer-btn">
                        Transfer {transferCount} Task{transferCount !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---- Client Transfer Card ---- */}
        <Card className="border-slate-200 shadow-sm" data-testid="client-transfer-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#10B981]" /> Client Transfer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">From</label>
                <Select value={clientFrom} onValueChange={v => { setClientFrom(v); setClientPreview(null); }}>
                  <SelectTrigger data-testid="client-from-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{nonSuperAdmins.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400 mb-2" />
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">To</label>
                <Select value={clientTo} onValueChange={v => { setClientTo(v); setClientPreview(null); }}>
                  <SelectTrigger data-testid="client-to-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{nonSuperAdmins.filter(u => u.id !== clientFrom).map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Client transfer only moves client ownership. Tasks will <strong>not</strong> be transferred automatically.</p>
            </div>
            <Button onClick={previewClients} className="w-full bg-[#10B981] hover:bg-[#059669]" data-testid="preview-clients-btn"
              disabled={!clientFrom || !clientTo || clientFrom === clientTo}>
              Preview Transfer
            </Button>

            {clientPreview && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="client-preview-list">
                <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b">
                  <span className="text-sm font-medium text-slate-700">
                    {clientPreview.from_user} <ArrowRight className="h-3 w-3 inline mx-1" /> {clientPreview.to_user}
                  </span>
                  <Badge variant="outline">{clientPreview.client_count} clients</Badge>
                </div>
                {clientPreview.clients.length === 0 ? (
                  <div className="p-4 text-sm text-slate-400 text-center">No clients to transfer</div>
                ) : (
                  <>
                    <div className="px-4 py-2 border-b bg-white flex items-center gap-2">
                      <Checkbox checked={clientSelectAll} onCheckedChange={toggleAllClients} data-testid="client-select-all" />
                      <span className="text-xs text-slate-500">Select All ({selectedClientIds.size}/{clientPreview.clients.length})</span>
                    </div>
                    <div className="max-h-[250px] overflow-y-auto">
                      {clientPreview.clients.map(c => (
                        <div key={c.id} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 hover:bg-slate-50" data-testid={`client-item-${c.id}`}>
                          <Checkbox checked={selectedClientIds.has(c.id)} onCheckedChange={() => toggleClientSelect(c.id)} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 truncate">{c.company}</p>
                            <p className="text-xs text-slate-400">{c.gst_number} · {c.branch}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-3 bg-slate-50 border-t">
                      <Button onClick={() => setShowClientConfirm(true)} className="w-full bg-[#10B981] hover:bg-[#059669]"
                        disabled={clientTransferCount === 0} data-testid="confirm-client-transfer-btn">
                        Transfer {clientTransferCount} Client{clientTransferCount !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Task Transfer Confirmation Dialog */}
      <Dialog open={showTaskConfirm} onOpenChange={setShowTaskConfirm}>
        <DialogContent className="max-w-md" data-testid="task-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckSquare className="h-5 w-5 text-[#5B6FD6]" /> Confirm Task Transfer</DialogTitle>
            <DialogDescription>This action will reassign tasks to a different employee.</DialogDescription>
          </DialogHeader>
          <div className="bg-[#5B6FD6]/5 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">From:</span>
              <span className="font-semibold text-slate-900">{getUserName(taskFrom)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">To:</span>
              <span className="font-semibold text-slate-900">{getUserName(taskTo)}</span>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
              <span className="text-slate-600">Tasks to transfer:</span>
              <span className="font-bold text-[#5B6FD6] text-lg">{transferCount}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskConfirm(false)}>Cancel</Button>
            <Button className="bg-[#5B6FD6] hover:bg-[#4A5BC4]" onClick={executeTaskTransfer}
              disabled={taskTransferring} data-testid="execute-task-transfer-btn">
              {taskTransferring ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Transferring...</> : 'Confirm Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Transfer Confirmation Dialog */}
      <Dialog open={showClientConfirm} onOpenChange={setShowClientConfirm}>
        <DialogContent className="max-w-md" data-testid="client-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[#10B981]" /> Confirm Client Transfer</DialogTitle>
            <DialogDescription>Only client ownership will be moved. Tasks will stay with the current assignee.</DialogDescription>
          </DialogHeader>
          <div className="bg-[#10B981]/5 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">From:</span>
              <span className="font-semibold text-slate-900">{getUserName(clientFrom)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">To:</span>
              <span className="font-semibold text-slate-900">{getUserName(clientTo)}</span>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
              <span className="text-slate-600">Clients to transfer:</span>
              <span className="font-bold text-[#10B981] text-lg">{clientTransferCount}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientConfirm(false)}>Cancel</Button>
            <Button className="bg-[#10B981] hover:bg-[#059669]" onClick={executeClientTransfer}
              disabled={clientTransferring} data-testid="execute-client-transfer-btn">
              {clientTransferring ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Transferring...</> : 'Confirm Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
